// src/screens/SubscriptionScreen.js
// ============================================================================
// PLANS & CRITS: subscription tiers + Crit balance + Crit packs
//
// Surfaces the post-pivot (2026-06-26) money model:
//   - Subscribe to a tier (Stripe Billing) -> monthly Crit grant
//   - Buy Crit packs (one-time top-ups)
//   - See current Crit balance + tier
//
// Backend callables used (see functions/modules/subscriptions.js + crits.js +
// payments/stripeDeposits.js): getMySubscription, getMyCrits,
// getCritPurchaseOptions, createSubscriptionCheckout, cancelMySubscription,
// createCritPurchaseCheckout. (Cancel is on-platform — no Stripe billing portal.)
//
// Renewal consent (evergreen 3.5a, design 13.7 / 14.1): Subscribe opens a
// confirm sheet (the renewal disclosure + an UNTICKED consent box) and only
// its "Continue to payment" calls createSubscriptionCheckout; every
// changeSubscriptionPlan call (Confirm, the new-card path, the declined-card
// retry) carries the consent ticked in the change modal. This screen is
// exempt from the Terms prompt (App.js HIDE_TOS_PROMPT_ROUTES) so cancel
// always works; while the stored Terms are behind it gates its own purchases.
// ============================================================================

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, SafeAreaView, Modal, Pressable, Platform, Animated, Easing,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { THEME } from '../constants/uiTheme';
import { useError } from '../components/ErrorContext';
import { RumbleBurst } from '../components/TsunamiMeter';
import Header from '../components/Header';
import ConsentCheckbox, { ConsentHint } from '../components/ConsentCheckbox';
import {
  planDisclosure, planConsentText, planErrorSentence, planPriceChangedDetails, RENEWAL_CONSENT_REFRESH,
} from '../components/UpgradeMembershipModal';
import { isTosVersionBehind } from '../constants/tosUpdate';
import useCritsEnabled from '../hooks/useCritsEnabled';

const C = THEME.colors;
const IS_WEB = Platform.OS === 'web';

const fmtPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// A tier's price for an interval, in cents (the tier list from getMySubscription).
const tierCentsFor = (t, interval) => (interval === 'year' ? t?.priceYearCents : t?.priceCents);

// ISO string / ms -> ms, or null.
const toMs = (v) => {
  const ms = typeof v === 'number' ? v : Date.parse(v || '');
  return Number.isFinite(ms) && ms > 0 ? ms : null;
};

const TERMS_BEHIND_TEXT = 'Review the updated Terms to buy or change a plan.';

// Tier ordering for upgrade vs downgrade detection.
const TIER_RANK = { free: 0, rookie: 1, pro: 2, elite: 3 };

// How long videos are kept, per tier (founder 2026-08-03 — replaces the old GB
// caps, which made members do storage math at the moment of purchase). Display
// copy — the enforced windows live in settings/videos (backend modules/vods.js,
// tierRetentionDays + yearlyRetentionBonus); keep in sync. Yearly DOUBLES the
// window: a number a member can hold in their head while choosing a plan.
const VIDEO_RETENTION = {
  // Free keeps stream videos 7 days once the Oct 2 settings/videos write lands
  // (tierRetentionDays.free 7, D-d); this ships in the same release. No yearly.
  free: { days: 7, daysYearly: 7 },
  rookie: { days: 7, daysYearly: 14 },
  pro: { days: 30, daysYearly: 60 },
  elite: { days: 90, daysYearly: 180 },
};

// Simulcast destination counts per tier — DISPLAY COPY ONLY; the enforced
// allowance is DEST_ALLOWANCE in functions/modules/restream.js. Keep in sync.
// The perk bullet renders only while settings/restream.enabled is true, so the
// membership page never sells a feature that is still dark.
const SIMULCAST_DESTINATIONS = {
  free: null,
  rookie: { month: 1, year: 2 },
  pro: { month: 2, year: 4 },
  elite: { month: 3, year: 6 },
};

// Redirect to a Stripe-hosted URL (web). Stripe returns the user to /account.
function goToStripe(url) {
  if (url && typeof window !== 'undefined') {
    window.location.href = url;
  }
}

// King Crit dice for the Crits card flair — multicolored (green/purple/default),
// faces 1 & 20 (same assets as the Rumble burst). ONE die per Crit the member
// holds (capped at 100), rolling in from the left, tumbling, and bounce-settling
// into a pile — the card literally fills with dice as your Crit balance grows.
const CRIT_DICE = [
  require('../../assets/20_gdice.webp'),
  require('../../assets/1_pdice.webp'),
  require('../../assets/20_pdice.webp'),
  require('../../assets/1_gdice.webp'),
  require('../../assets/20_dice.webp'),
  require('../../assets/1_dice.webp'),
];

const DICE_CAP = 100;       // never render more than this many dice
const DICE_FILL = 0.55;     // target fraction of the pile area covered by dice
const DIE_MIN = 12, DIE_MAX = 46;
const CARD_PAD = 14;
// Deterministic pseudo-random in [0,1) so each die's resting spot + spin is
// stable across re-renders (no Math.random churn on every paint).
const seeded = (n) => { const s = Math.sin((n + 1) * 999.7) * 43758.5453; return s - Math.floor(s); };

// crits → dice, sized to FILL the pile area (measured from the card). The pile
// occupies the right of the card; the left is reserved for the number/text. On a
// wide desktop card the dice spread out bigger; on a narrow phone card they pack
// into the smaller space — same "≈55% covered" look either way.
function CritsDiceRoll({ crits, cardW, cardH }) {
  const count = Math.min(DICE_CAP, Math.max(0, Math.floor(crits || 0)));
  const textReserve = Math.min(0.42 * cardW, 230);
  const areaW = Math.max(60, cardW - textReserve - CARD_PAD);
  const areaH = Math.max(48, cardH - CARD_PAD);
  const size = count > 0
    ? Math.max(DIE_MIN, Math.min(DIE_MAX, Math.round(Math.sqrt((areaW * areaH * DICE_FILL) / count))))
    : 20;

  // Rebuild (fresh Animated values → re-pour) whenever the count OR the measured
  // area/size changes: buying Crits adds dice; resizing re-fits + re-pours.
  const dice = useMemo(() => Array.from({ length: count }, (_, i) => ({
    face: CRIT_DICE[i % CRIT_DICE.length],
    left: Math.round(seeded(i * 2) * Math.max(1, areaW - size)),
    top: Math.round(seeded(i * 2 + 1) * Math.max(1, areaH - size)),
    rotStart: -110 - Math.round(seeded(i * 3) * 80),
    rotEnd: 320 + Math.round(seeded(i * 5) * 160),
    x: new Animated.Value(-Math.max(220, areaW * 0.7)),
    y: new Animated.Value(0),
    r: new Animated.Value(0),
    o: new Animated.Value(0),
  })), [count, areaW, areaH, size]);

  useEffect(() => {
    if (!dice.length) return undefined;
    // Slower, deliberate pour: whole cascade ~2.6s regardless of count.
    const stagger = Math.min(90, Math.max(16, Math.floor(2600 / dice.length)));
    const runs = dice.map((d) => Animated.sequence([
      Animated.parallel([
        Animated.timing(d.o, { toValue: 1, duration: 140, useNativeDriver: true }),
        Animated.timing(d.x, { toValue: 0, duration: 900, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
        Animated.timing(d.r, { toValue: 1, duration: 900, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      ]),
      // Land: a little hop, then spring back down (the craps-table bounce).
      Animated.timing(d.y, { toValue: -12, duration: 120, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      Animated.spring(d.y, { toValue: 0, friction: 5, tension: 150, useNativeDriver: true }),
    ]));
    const anim = Animated.stagger(stagger, runs);
    anim.start();
    return () => { try { anim.stop(); } catch (e) { /* noop */ } };
  }, [dice]);

  if (!cardW || !dice.length) return null; // wait for the card to be measured
  return (
    <View pointerEvents="none" style={[diceStyles.wrap, { width: areaW, right: CARD_PAD }]}>
      {dice.map((d, i) => (
        <Animated.Image
          key={i}
          source={d.face}
          style={{
            position: 'absolute',
            width: size,
            height: size,
            resizeMode: 'contain',
            left: d.left,
            top: d.top,
            opacity: d.o,
            transform: [
              { translateX: d.x },
              { translateY: d.y },
              { rotate: d.r.interpolate({ inputRange: [0, 1], outputRange: [`${d.rotStart}deg`, `${d.rotEnd}deg`] }) },
            ],
          }}
        />
      ))}
    </View>
  );
}

const diceStyles = StyleSheet.create({
  // Pile area on the right of the Crits card; behind the text (rendered first).
  // Width/right set inline from the measured card. overflow:hidden clips the
  // roll-in from the card's edge.
  wrap: { position: 'absolute', top: 0, bottom: 0, justifyContent: 'center' },
});

export default function SubscriptionScreen({ navigation }) {
  const { showError } = useError();
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled

  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null); // getMySubscription result
  const [crits, setCrits] = useState(null); // getMyCrits result
  const [packs, setPacks] = useState([]); // getCritPurchaseOptions result
  const [founder, setFounder] = useState(null); // getMyBadges result (Founder-badge scarcity)
  const [billing, setBilling] = useState('month'); // 'month' | 'year' (yearly = 4 months free + a 75% bigger MONTHLY Crit grant:
  // 40->70 / 100->175 / 250->440 per month, not 20% - verified against an issued
  // grant 2026-08-03. The bullets below render the real number from the tier
  // config, so they were never wrong; this comment was.)
  const [busy, setBusy] = useState(null); // id of the action in flight
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey, title }
  const [burst, setBurst] = useState(null); // { title, subtitle } | null — success dice rain
  // { t, kind: 'upgrade'|'downgrade', isIntervalSwitch, billing, declined?, price?: {amountCents, interval}
  // (the server's, after plan-price-changed), setupSessionId? (a saved new card) } awaiting confirm
  const [pendingChange, setPendingChange] = useState(null);
  const [changeConsent, setChangeConsent] = useState(false); // change modal's renewal consent box; UNTICKED on every open
  // Subscribe confirm sheet: { t, interval, amountCents, note } (amount/interval
  // are the server's after plan-price-changed); its consent box starts UNTICKED.
  const [planConfirm, setPlanConfirm] = useState(null);
  const [planConsent, setPlanConsent] = useState(false);
  const [termsBehind, setTermsBehind] = useState(false); // stored Terms version behind this bundle's
  const [pendingCancel, setPendingCancel] = useState(false); // cancel-plan confirm modal
  const [cardSize, setCardSize] = useState({ w: 0, h: 0 }); // measured Crits card → responsive dice pile
  const [pmOnFile, setPmOnFile] = useState(null); // { brand, last4 } — card the upgrade would charge
  const [simulcastLive, setSimulcastLive] = useState(false); // settings/restream.enabled — gates the perk bullet
  const isFocused = useIsFocused();
  // { t, sessionId, renewalConsent, billing, isIntervalSwitch }: upgrade waiting on a new-card setup
  const cardUpgradeRef = useRef(null);
  const applyRef = useRef(null); // stable handle so the checkout onComplete closure stays fresh
  const checkoutContainerRef = useRef(null);
  const checkoutInstanceRef = useRef(null);
  const prevRef = useRef({ tier: 'free', crits: 0, kind: null }); // snapshot before checkout (for poll + flair)
  // The Stripe onComplete closure is built once per checkout (adding critsEnabled
  // to that effect's deps would tear down a live checkout), so it reads the flag
  // through this ref to stay current if the flag flips mid-checkout.
  const critsEnabledRef = useRef(critsEnabled);
  useEffect(() => { critsEnabledRef.current = critsEnabled; }, [critsEnabled]);

  // After a successful payment the Stripe webhook updates tier/Crits async —
  // poll a few times so the screen reflects it without a manual refresh.
  const pollForUpdate = useCallback(async (prev) => {
    const functions = getFunctions();
    const getSub = httpsCallable(functions, 'getMySubscription');
    const getCrits = httpsCallable(functions, 'getMyCrits');
    for (let i = 0; i < 6; i++) {
      await new Promise((r) => setTimeout(r, 1800));
      try {
        const [s, c] = await Promise.all([getSub(), getCrits()]);
        if (s?.data) setSub(s.data);
        if (c?.data) setCrits(c.data);
        const changed = (s?.data?.tier && s.data.tier !== prev.tier)
          || (Number(c?.data?.critBalance || 0) !== prev.crits);
        if (changed) break;
      } catch (e) { /* keep polling */ }
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const functions = getFunctions();
      const getSub = httpsCallable(functions, 'getMySubscription');
      const getCrits = httpsCallable(functions, 'getMyCrits');
      const getPacks = httpsCallable(functions, 'getCritPurchaseOptions');
      const getBadges = httpsCallable(functions, 'getMyBadges');

      const [subRes, critRes, packRes, badgeRes] = await Promise.all([
        getSub(),
        getCrits(),
        getPacks().catch(() => ({ data: { packs: [] } })),
        getBadges().catch(() => ({ data: null })),
      ]);

      setSub(subRes.data);
      // Open the screen showing the member's ACTUAL billing interval, so their
      // real plan reads as "Current plan" (not the opposite interval's price).
      setBilling(subRes.data?.interval === 'year' ? 'year' : 'month');
      setCrits(critRes.data);
      setPacks(packRes.data?.packs || []);
      setFounder(badgeRes.data || null);
    } catch (err) {
      console.error('[SubscriptionScreen] load failed:', err);
      showError('Could not load your plan', err?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  // Simulcast perk copy is gated on the live product flag (settings/restream is
  // world-readable) so this page never advertises a destination feature that is
  // still dark. Flipping the flag makes the bullet appear with no redeploy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(), 'settings', 'restream'));
        if (!cancelled) setSimulcastLive(snap.exists() && snap.data()?.enabled === true);
      } catch {
        if (!cancelled) setSimulcastLive(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // This screen is exempt from the Terms prompt, so it gates its own purchases
  // (13.7): one read of the users doc on each focus, the App's predicate
  // (a stored version behind this bundle's, finished accounts only). Any
  // failure leaves purchases open, like the App gate; cancel never depends on it.
  useEffect(() => {
    if (!isFocused) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        await auth.authStateReady?.();
        const uid = auth.currentUser?.uid;
        if (!uid) { if (!cancelled) setTermsBehind(false); return; }
        const { getFirestore, doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(), 'users', uid));
        const data = snap.exists() ? (snap.data() || {}) : null;
        const accepted = data?.verification?.tos?.version;
        if (!cancelled) {
          setTermsBehind(!!accepted && data.onboardingComplete !== false && isTosVersionBehind(accepted));
        }
      } catch (e) {
        if (!cancelled) setTermsBehind(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isFocused]);

  // Every open of the change modal starts with its consent box unticked.
  const openChangeModal = (pc) => {
    setChangeConsent(false);
    setPendingChange(pc);
  };

  // Open the on-screen embedded Stripe checkout (web), or fall back to a hosted
  // redirect on native. Mirrors the Rumble flow — keeps the user on this screen.
  // `disclosure` (plans only) is printed above the mounted payment form.
  const openCheckout = (d, title, kind, disclosure = null) => {
    // Snapshot current state so we can poll for the post-webhook change + flair.
    prevRef.current = { tier: sub?.tier || 'free', crits: Number(crits?.critBalance || 0), kind };
    if (IS_WEB && d?.clientSecret) {
      setEmbedded({ clientSecret: d.clientSecret, publishableKey: d.publishableKey, title, disclosure });
    } else if (d?.url) {
      goToStripe(d.url);
    } else {
      throw new Error('Could not start checkout.');
    }
  };

  // Subscribe on a plan card only opens the confirm sheet; nothing is created yet.
  const openPlanConfirm = (t) => {
    if (termsBehind) return;
    setPlanConsent(false);
    setPlanConfirm({ t, interval: billing, amountCents: tierCentsFor(t, billing), note: null });
  };

  const closePlanConfirm = () => {
    if (busy) return;
    setPlanConfirm(null);
    setPlanConsent(false);
  };

  // The only caller of createSubscriptionCheckout on this screen: runs from the
  // confirm sheet's "Continue to payment", disabled until the box is ticked.
  // Covers both the embedded form and the hosted redirect (d.url).
  const subscribe = async () => {
    const pc = planConfirm;
    if (!pc?.t || !planConsent || termsBehind || busy) return;
    const tierKey = pc.t.key;
    if (!Number.isInteger(pc.amountCents) || pc.amountCents <= 0) {
      showError('Could not start checkout', 'Please refresh the page and try again.');
      return;
    }
    setBusy(`sub:${tierKey}`);
    try {
      const fn = httpsCallable(getFunctions(), 'createSubscriptionCheckout');
      const res = await fn({
        tier: tierKey,
        interval: pc.interval,
        renewalConsent: { accepted: true, amountCents: pc.amountCents, interval: pc.interval, source: 'plans_page' },
      });
      const disclosure = res.data?.disclosure || planDisclosure(pc.t.name, pc.amountCents, pc.interval);
      openCheckout(res.data, 'Start your plan', 'sub', disclosure);
      setPlanConfirm(null);
      setPlanConsent(false);
    } catch (err) {
      console.error('[SubscriptionScreen] subscribe failed:', err);
      const msg = String(err?.message || '');
      const changed = planPriceChangedDetails(err);
      if (changed) {
        // The price moved: re-render the terms and the box with the server's
        // price, unticked.
        setPlanConsent(false);
        setPlanConfirm({ ...pc, amountCents: changed.amountCents, interval: changed.interval, note: planErrorSentence(err) });
        return;
      }
      if (msg.includes('renewal-consent-required')) {
        setPlanConsent(false);
        setPlanConfirm({ ...pc, note: RENEWAL_CONSENT_REFRESH });
        return;
      }
      setPlanConfirm(null);
      setPlanConsent(false);
      // Backend refuses a 2nd subscription for an active member — refresh state
      // and point them at in-app plan changes instead of a new checkout.
      if (msg.includes('existing-subscription')) {
        load();
        showError('You already have a paid plan', 'Use "Change to…" on another plan to switch.');
        return;
      }
      showError('Could not start checkout', planErrorSentence(err));
    } finally {
      setBusy(null);
    }
  };

  const closeEmbedded = useCallback(() => {
    try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
    checkoutInstanceRef.current = null;
    setEmbedded(null);
  }, []);

  // Mount the embedded checkout whenever we have a clientSecret + a container.
  useEffect(() => {
    if (!IS_WEB || !embedded?.clientSecret) return;
    let cancelled = false;
    (async () => {
      try {
        if (typeof embedded.publishableKey !== 'string' || !embedded.publishableKey) {
          throw new Error('Stripe publishable key is not configured.');
        }
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(embedded.publishableKey);
        if (cancelled || !stripe) return;
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: embedded.clientSecret,
          onComplete: () => {
            // Payment captured — tear down, rain the dice, and poll for the
            // post-webhook tier/Crit update (no manual refresh needed).
            const prev = prevRef.current;
            try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
            checkoutInstanceRef.current = null;
            setEmbedded(null);
            if (prev.kind === 'card') {
              // New card saved (setup mode — nothing charged yet). Apply the
              // upgrade that was waiting on it, charging the new card, with
              // the consent ticked before the card form opened.
              const job = cardUpgradeRef.current;
              cardUpgradeRef.current = null;
              if (job?.t) {
                applyRef.current?.(job.t, {
                  setupSessionId: job.sessionId,
                  renewalConsent: job.renewalConsent,
                  change: { kind: 'upgrade', isIntervalSwitch: job.isIntervalSwitch, billing: job.billing },
                });
              }
              return;
            }
            setBurst(prev.kind === 'sub'
              ? { title: "You're in!", subtitle: critsEnabledRef.current ? 'Plan activating, Crits incoming.' : 'Plan activating.' }
              : { title: 'Nice!', subtitle: 'Crits incoming!' });
            pollForUpdate(prev);
          },
        });
        if (cancelled) { try { instance.destroy(); } catch (e) { /* noop */ } return; }
        checkoutInstanceRef.current = instance;
        if (checkoutContainerRef.current) instance.mount(checkoutContainerRef.current);
      } catch (err) {
        console.error('[SubscriptionScreen] embedded checkout mount failed:', err);
        if (!cancelled) {
          setEmbedded(null);
          showError('Could not open checkout', 'Please try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
      try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
      checkoutInstanceRef.current = null;
    };
  }, [embedded, pollForUpdate, showError]);

  // Cancel on-platform (no Stripe portal redirect) — mirrors the keep-everything-
  // here principle. Backend sets cancel_at_period_end; benefits stay until the
  // period ends, then the member drops to Free. No charge after that.
  const cancelSubscription = async () => {
    setBusy('cancel');
    try {
      await httpsCallable(getFunctions(), 'cancelMySubscription')({});
      setPendingCancel(false);
      setBurst({ title: 'Plan canceled', subtitle: 'Your benefits stay until the period ends.' });
      await load();
    } catch (err) {
      console.error('[SubscriptionScreen] cancel failed:', err);
      showError('Could not cancel your plan', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // Existing subscribers change plans IN-APP — no checkout, no redirect. The
  // backend edits the one subscription on the card already on file (upgrade =
  // now, downgrade = at period end). Never a second subscription. Every change
  // confirms in a modal first — upgrades charge + grant Crits immediately.
  const changePlan = (t) => {
    // Classify the same way the backend does. A SAME-TIER interval switch goes by
    // commitment: to yearly = UPGRADE (applies now, prorated charge + Crit
    // top-up), to monthly = downgrade (deferred). Tier changes go by
    // monthly-normalized price. Matches subscriptions.js.
    const monthlyOf = (obj, iv) => {
      if (!obj || !obj.paid) return 0;
      return iv === 'year' ? Math.round((obj.priceYearCents || 0) / 12) : (obj.priceCents || 0);
    };
    const curObj = tiers.find((x) => x.key === (sub?.tier || 'free'));
    const isIntervalSwitch = t.key === (sub?.tier || 'free'); // same tier, other interval
    const isDowngrade = isIntervalSwitch
      ? (billing === 'month')
      : monthlyOf(t, billing) < monthlyOf(curObj, sub?.interval || 'month');
    if (termsBehind) return;
    openChangeModal({ t, kind: isDowngrade ? 'downgrade' : 'upgrade', isIntervalSwitch, billing });
    if (!isDowngrade) {
      // Show which card the upgrade will charge (non-blocking).
      setPmOnFile(null);
      httpsCallable(getFunctions(), 'getMyPaymentMethod')({})
        .then((r) => setPmOnFile(r.data?.paymentMethod || null))
        .catch(() => {});
    }
  };

  // "Use a different card": save a new card in the same on-screen embedded
  // Stripe modal used for Crit packs (setup mode — no charge), then apply the
  // upgrade against it. Never leaves the platform. The consent ticked in the
  // change modal is captured BEFORE the modal closes and rides along with the
  // card setup, so the new-card upgrade always carries it.
  const startCardUpdate = async (pc, renewalConsent) => {
    const t = pc?.t;
    if (!t || !renewalConsent || termsBehind) return;
    const job = { t, renewalConsent, billing: pc.billing || billing, isIntervalSwitch: !!pc.isIntervalSwitch };
    setPendingChange(null);
    setBusy(`sub:${t.key}`);
    try {
      const res = await httpsCallable(getFunctions(), 'createCardSetupSession')({});
      cardUpgradeRef.current = { ...job, sessionId: res.data?.sessionId };
      openCheckout(res.data, 'Update payment method', 'card');
    } catch (err) {
      console.error('[SubscriptionScreen] card setup failed:', err);
      showError('Could not open card update', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  // Per-month Crit grant preview (mirrors the backend's math) for the modal.
  const perMonthGrant = (tierObj, interval) =>
    (interval === 'year' ? (tierObj?.critGrantYearly || tierObj?.critGrant || 0) : (tierObj?.critGrant || 0));
  const upgradeCritPreview = (t) => {
    const cur = tiers.find((x) => x.key === (sub?.tier || 'free'));
    return Math.max(0, perMonthGrant(t, billing) - perMonthGrant(cur, sub?.interval || 'month'));
  };

  // The change modal's renewal terms for a pending change: the new term's
  // price from the tier list (or the server's after plan-price-changed), the
  // 13.7 disclosure (immediate, or "Starting {date}, ..." for a change that
  // waits for the next billing date) and the consent sentence.
  const changeTerms = (pc) => {
    if (!pc?.t) return null;
    const interval = pc.price?.interval || pc.billing || billing;
    const amountCents = pc.price?.amountCents ?? tierCentsFor(pc.t, interval);
    const scheduled = pc.kind !== 'upgrade';
    const startsAtMs = scheduled ? toMs(sub?.currentPeriodEnd) : null;
    return {
      interval,
      amountCents,
      disclosure: planDisclosure(pc.t.name, amountCents, interval, {
        change: true, startsAtMs, startsAtNextBilling: scheduled && !startsAtMs,
      }),
      consentText: planConsentText(interval, amountCents),
    };
  };

  // The consent for the pending change, only once its box is ticked.
  const changeRenewalConsent = (pc) => {
    const terms = changeTerms(pc);
    if (!changeConsent || !terms || !Number.isInteger(terms.amountCents) || terms.amountCents <= 0) return null;
    return { accepted: true, amountCents: terms.amountCents, interval: terms.interval, source: 'change_modal' };
  };

  // The only caller of changeSubscriptionPlan. `renewalConsent` comes from the
  // change modal's box (Confirm, or through the new-card setup); `change`
  // carries the modal's shape so a refusal can reopen it.
  const applyPlanChange = async (t, { setupSessionId = null, renewalConsent = null, change = null } = {}) => {
    const iv = change?.billing || billing;
    setBusy(`sub:${t.key}`);
    try {
      const res = await httpsCallable(getFunctions(), 'changeSubscriptionPlan')({
        tier: t.key,
        interval: iv,
        ...(setupSessionId ? { setupSessionId } : {}),
        renewalConsent,
      });
      if (res.data?.effective === 'period_end') {
        // Downgrade scheduled — tier is unchanged until the period ends.
        const when = res.data?.effectiveAt
          ? new Date(res.data.effectiveAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : 'your next billing date';
        setBurst({ title: 'Change scheduled', subtitle: `You'll switch to ${t.name} on ${when}.` });
      } else {
        // Upgrade applied now — Crits were granted server-side.
        const added = Number(res.data?.critsAdded || 0);
        setBurst({
          title: `You're on ${t.name}!`,
          subtitle: added > 0 ? `${added} Crits added to your balance.` : 'Your new plan is active.',
        });
        pollForUpdate({ tier: sub?.tier || 'free', crits: Number(crits?.critBalance || 0), kind: 'sub' });
      }
      load();
    } catch (err) {
      console.error('[SubscriptionScreen] changePlan failed:', err);
      const msg = String(err?.message || '');
      const changed = planPriceChangedDetails(err);
      if (msg.includes('card-declined')) {
        // Charge declined — Stripe left the subscription untouched and nothing
        // was granted. Reopen the confirm modal (consent unticked) with the
        // decline notice so the member can retry with a different card
        // without losing their place.
        openChangeModal({ t, kind: 'upgrade', isIntervalSwitch: !!change?.isIntervalSwitch, billing: iv, declined: true });
      } else if (changed) {
        // The price moved: reopen the modal with the server's price, unticked,
        // keeping a saved new card for the retry.
        openChangeModal({
          t,
          kind: change?.kind || 'upgrade',
          isIntervalSwitch: !!change?.isIntervalSwitch,
          billing: iv,
          price: changed,
          ...(setupSessionId ? { setupSessionId } : {}),
        });
        showError('The price changed', planErrorSentence(err));
      } else if (msg.includes('renewal-consent-required')) {
        showError('Could not change plan', RENEWAL_CONSENT_REFRESH);
      } else if (msg.includes('already-on-plan')) {
        showError('No change needed', 'You are already on this plan.');
      } else {
        showError('Could not change plan', planErrorSentence(err));
      }
    } finally {
      setBusy(null);
    }
  };
  applyRef.current = applyPlanChange;

  const buyPack = async (packId) => {
    if (termsBehind) return;
    setBusy(`pack:${packId}`);
    try {
      const fn = httpsCallable(getFunctions(), 'createCritPurchaseCheckout');
      const res = await fn({ packId });
      openCheckout(res.data, 'Buy Crits', 'pack');
    } catch (err) {
      console.error('[SubscriptionScreen] buy pack failed:', err);
      showError('Could not start purchase', err?.message || 'Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const currentTier = sub?.tier || 'free';
  const tiers = sub?.tiers || [];
  const pendingChangeIv = pendingChange?.billing || billing; // the interval the pending change moves to
  const pendingTerms = changeTerms(pendingChange); // its renewal disclosure + consent text

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={C.brand} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scroll}>
          <Text style={styles.pageTitle}>{critsEnabled ? 'Plans & Crits' : 'Plans'}</Text>
          {/* Stored Terms behind: purchases and plan changes wait for the
              review; cancel, keep and viewing always work (13.7). */}
          {termsBehind && (
            <View style={styles.termsBanner}>
              <Ionicons name="document-text-outline" size={18} color={C.brand} style={{ marginTop: 1 }} />
              <Text style={styles.termsBannerTxt}>{TERMS_BEHIND_TEXT}</Text>
              <TouchableOpacity
                style={styles.termsBannerBtn}
                onPress={() => navigation.navigate('TosAgreement')}
                accessibilityRole="button"
              >
                <Text style={styles.termsBannerBtnTxt}>Review Terms</Text>
              </TouchableOpacity>
            </View>
          )}
          {/* Current status — the Crit balance + dice pile hide when the Crit
              economy flag is off; the membership line stays. */}
          <View
            style={styles.statusCard}
            onLayout={(e) => {
              const { width, height } = e.nativeEvent.layout;
              setCardSize((p) => (p.w === width && p.h === height ? p : { w: width, h: height }));
            }}
          >
            {critsEnabled && (
              <CritsDiceRoll crits={Number(crits?.critBalance || 0)} cardW={cardSize.w} cardH={cardSize.h} />
            )}
            <View style={{ flex: 1 }}>
              {critsEnabled && (
                <>
                  <Text style={styles.statusLabel}>Your Crits</Text>
                  <Text style={styles.critBalance}>{Number(crits?.critBalance || 0).toLocaleString()}</Text>
                </>
              )}
              <Text style={styles.statusSub}>
                {currentTier === 'free' ? 'Free plan' : `${sub?.tierName || currentTier} plan${sub?.interval === 'year' ? ' · yearly' : ''}`}
              </Text>
            </View>
          </View>

          {/* Tiers */}
          <Text style={styles.sectionTitle}>Choose a plan</Text>
          <Text style={styles.sectionHint}>
            {critsEnabled
              ? "Monthly Crits + videos: your streams are kept for your plan's window and published on your profile."
              : "Videos: your streams are kept for your plan's window, and you can publish them to your profile."}
            {simulcastLive ? ' Paid plans also simulcast your stream out to Twitch, YouTube, Kick and more.' : ''} Cancel anytime.
          </Text>

          {/* Monthly / Yearly toggle — yearly = 4 months free + a 75% bigger monthly Crit grant */}
          <View style={styles.billingToggle}>
            {[['month', 'Monthly'], ['year', 'Yearly']].map(([k, l]) => (
              <TouchableOpacity
                key={k}
                style={[styles.billingOpt, billing === k && styles.billingOptOn]}
                onPress={() => setBilling(k)}
              >
                <Text style={[styles.billingOptTxt, billing === k && styles.billingOptTxtOn]}>
                  {l}{k === 'year' ? '  · 4 months free' : ''}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Founder-badge scarcity — the first 1,000 (yearly any tier, or
              monthly Pro/Elite) earn the badge. Hidden once earned or sold out. */}
          {founder && !founder.founderNumber && (founder.remaining || 0) > 0 && (
            <View style={styles.founderScarcity}>
              <Ionicons name="ribbon" size={18} color="#aff0ff" style={{ marginTop: 1 }} />
              <Text style={styles.founderScarcityTxt}>
                <Text style={styles.founderScarcityStrong}>
                  {Number(founder.remaining).toLocaleString()} of {Number(founder.cap || 1000).toLocaleString()} Founder badges left.
                </Text>
                {' '}The first {Number(founder.cap || 1000).toLocaleString()} people to go yearly on any plan, or
                monthly on Pro or Elite, earn the animated Founder badge — yours for life.
              </Text>
            </View>
          )}

          {tiers.map((t) => {
            const isCurrentTier = t.key === currentTier;
            const actualInterval = sub?.interval === 'year' ? 'year' : 'month';
            const isCurrentPlan = isCurrentTier && billing === actualInterval; // tier AND interval
            const pendingMatch = sub?.pendingPlanChange?.tier === t.key
              && (sub?.pendingPlanChange?.interval === 'year' ? 'year' : 'month') === billing;
            const isIntervalSwitch = isCurrentTier && !isCurrentPlan && !pendingMatch && t.paid; // same tier, other interval
            const inFlight = busy === `sub:${t.key}`;
            const yearly = billing === 'year' && !!t.priceYearCents;
            const grant = yearly ? (t.critGrantYearly || t.critGrant) : t.critGrant;
            return (
              <View key={t.key} style={[styles.tierCard, isCurrentTier && styles.tierCardCurrent]}>
                <View style={styles.tierTop}>
                  <Text style={styles.tierName}>{t.name}</Text>
                  <Text style={styles.tierPrice}>
                    {!t.paid ? 'Free' : yearly ? `${fmtPrice(t.priceYearCents)}/yr` : `${fmtPrice(t.priceCents)}/mo`}
                  </Text>
                </View>
                {yearly && t.paid && (
                  <Text style={styles.yearlyNote}>
                    {fmtPrice(Math.round(t.priceYearCents / 12))}/mo equivalent — 4 months free vs monthly
                  </Text>
                )}
                <View style={styles.tierPerks}>
                  {critsEnabled && (
                    <Text style={styles.perk}>
                      • {grant} Crits / month{yearly && t.paid ? '  (yearly bonus, delivered monthly)' : ''}
                    </Text>
                  )}
                  {t.key === 'free' && VIDEO_RETENTION.free ? (
                    // The free plan keeps stream videos for a short window;
                    // only that is claimed here (no publish/download/simulcast
                    // perk lines, no yearly doubling).
                    <Text style={styles.perk}>• Stream videos kept for {VIDEO_RETENTION.free.days} days</Text>
                  ) : VIDEO_RETENTION[t.key] ? (
                    <>
                      <Text style={styles.perk}>
                        • Videos kept for {yearly ? VIDEO_RETENTION[t.key].daysYearly : VIDEO_RETENTION[t.key].days} days
                        {yearly ? '  (double, yearly bonus)' : ''}
                      </Text>
                      <Text style={styles.perk}>• Publish videos to your profile — likes, comments &amp; view stats</Text>
                      <Text style={styles.perk}>• Download your videos</Text>
                      {simulcastLive && SIMULCAST_DESTINATIONS[t.key] ? (
                        <Text style={styles.perk}>
                          • Simulcast to {yearly ? SIMULCAST_DESTINATIONS[t.key].year : SIMULCAST_DESTINATIONS[t.key].month} platform
                          {(yearly ? SIMULCAST_DESTINATIONS[t.key].year : SIMULCAST_DESTINATIONS[t.key].month) === 1 ? '' : 's'}
                          {' '}— go live on Twitch, YouTube, Kick and more at the same time
                          {yearly ? '  (double destinations, yearly bonus)' : ''}
                        </Text>
                      ) : null}
                    </>
                  ) : (
                    <Text style={styles.perk}>• No videos kept — streams available 72 h for match review only</Text>
                  )}
                </View>
                {isCurrentPlan ? (
                  <View style={styles.currentRow}>
                    <View style={styles.currentBadge}>
                      <Ionicons name="checkmark-circle" size={16} color={C.brand} />
                      <Text style={styles.currentBadgeText}>Current plan{sub?.interval === 'year' ? ' · yearly' : ' · monthly'}</Text>
                    </View>
                    {/* Paid tiers get an on-platform cancel next to the badge; the
                        free tier shows nothing. Once canceling, show when it ends. */}
                    {t.paid && (
                      sub?.cancelAtPeriodEnd ? (
                        <Text style={styles.cancelScheduled}>
                          Cancels {sub.currentPeriodEnd
                            ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'at period end'}
                        </Text>
                      ) : (
                        <TouchableOpacity onPress={() => setPendingCancel(true)} disabled={!!busy} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                          <Text style={styles.cancelLink}>Cancel plan</Text>
                        </TouchableOpacity>
                      )
                    )}
                  </View>
                ) : pendingMatch ? (
                  <View style={styles.currentBadge}>
                    <Ionicons name="time-outline" size={16} color={C.brand} />
                    <Text style={styles.currentBadgeText}>
                      Scheduled — switches{' '}
                      {sub.pendingPlanChange.effectiveAt
                        ? new Date(sub.pendingPlanChange.effectiveAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'at your next billing date'}
                    </Text>
                  </View>
                ) : isIntervalSwitch ? (
                  // Same tier, other interval — switch monthly↔yearly (backend
                  // schedules or prorates; the confirm modal explains which).
                  <TouchableOpacity
                    style={[styles.subscribeBtn, termsBehind && styles.btnDisabled]}
                    onPress={() => changePlan(t)}
                    disabled={!!busy || termsBehind}
                  >
                    {inFlight
                      ? <ActivityIndicator size="small" color="#000" />
                      : <Text style={styles.subscribeBtnText}>Switch to {billing === 'year' ? 'Yearly' : 'Monthly'}</Text>}
                  </TouchableOpacity>
                ) : t.paid ? (
                  <TouchableOpacity
                    style={[styles.subscribeBtn, termsBehind && styles.btnDisabled]}
                    onPress={() => (currentTier === 'free' ? openPlanConfirm(t) : changePlan(t))}
                    disabled={!!busy || termsBehind}
                  >
                    {inFlight
                      ? <ActivityIndicator size="small" color="#000" />
                      : <Text style={styles.subscribeBtnText}>{currentTier === 'free' ? 'Subscribe' : 'Change to ' + t.name}</Text>}
                  </TouchableOpacity>
                ) : null}
              </View>
            );
          })}

          {/* Crit packs — hidden when the Crit economy flag is off */}
          {critsEnabled && packs.length > 0 && (
            <>
              <Text style={[styles.sectionTitle, { marginTop: 24 }]}>Buy Crits</Text>
              <Text style={styles.sectionHint}>One-time top-ups. Subscriptions are the better per-Crit value.</Text>
              {packs.map((p) => {
                const inFlight = busy === `pack:${p.id}`;
                return (
                  <View key={p.id} style={styles.packCard}>
                    <View style={{ flex: 1, paddingRight: 12 }}>
                      <Text style={styles.packName}>{p.name} — {p.crits} Crits</Text>
                      {!!p.description && <Text style={styles.packDesc}>{p.description}</Text>}
                    </View>
                    <TouchableOpacity
                      style={[styles.buyBtn, termsBehind && styles.btnDisabled]}
                      onPress={() => buyPack(p.id)}
                      disabled={!!busy || termsBehind}
                    >
                      {inFlight
                        ? <ActivityIndicator size="small" color="#000" />
                        : <Text style={styles.buyBtnText}>{fmtPrice(p.priceCents)}</Text>}
                    </TouchableOpacity>
                  </View>
                );
              })}
            </>
          )}

          {critsEnabled && (
            <Text style={styles.footnote}>
              Crits are how you enter events — not cash, and can&apos;t be withdrawn.
              Crits from your plan and packs are yours to keep; Crits you win competing reset at the
              start of each season (every 2 months) for a fresh leaderboard. Crits are delivered when your
              payment clears and aren&apos;t refundable.
            </Text>
          )}
        </ScrollView>
      )}

      {/* On-screen Stripe embedded checkout — keeps the user on this screen
          (no redirect), mirroring the Rumble flow. */}
      {IS_WEB && embedded && (
        <Modal visible transparent animationType="fade" onRequestClose={closeEmbedded}>
          <View style={styles.checkoutOverlay}>
            <Pressable style={styles.checkoutScrim} onPress={closeEmbedded} />
            <View style={styles.checkoutCard}>
              <View style={styles.checkoutHeader}>
                <Text style={styles.checkoutTitle}>{embedded.title || 'Checkout'}</Text>
                <Pressable onPress={closeEmbedded} hitSlop={8}>
                  <Text style={styles.checkoutClose}>✕</Text>
                </Pressable>
              </View>
              <ScrollView style={styles.checkoutScroll} showsVerticalScrollIndicator={false}>
                {/* Plans: the server's renewal disclosure stays above the
                    payment form (14.1). */}
                {!!embedded.disclosure && <Text style={styles.checkoutDisclosure}>{embedded.disclosure}</Text>}
                {/* RN Web renders raw DOM nodes; Stripe mounts the embedded
                    checkout iframe into this div. */}
                <div ref={checkoutContainerRef} style={{ width: '100%', minHeight: 360 }} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* Plan-change confirmation: every change confirms here. Upgrades charge
          the prorated difference now and add Crits instantly; downgrades keep
          current benefits until the period ends and add no Crits. Above
          Confirm, the new term's renewal disclosure and an UNTICKED consent
          box that gates BOTH Confirm and "Use a different card" (13.7). */}
      <Modal visible={!!pendingChange} transparent animationType="fade" onRequestClose={() => setPendingChange(null)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setPendingChange(null)} />
          <View style={styles.dgCard}>
            {pendingChange?.kind === 'upgrade' ? (
              <>
                <Text style={styles.dgTitle}>
                  {pendingChange?.isIntervalSwitch
                    ? `Switch to ${pendingChangeIv === 'year' ? 'yearly' : 'monthly'} billing?`
                    : `Upgrade to ${pendingChange?.t?.name}?`}
                </Text>
                {pendingChange?.declined && (
                  <View style={styles.dgDeclined}>
                    <Ionicons name="alert-circle" size={16} color="#ff6b6b" />
                    <Text style={styles.dgDeclinedTxt}>
                      Your card was declined. Nothing was changed or charged. Try a different card.
                    </Text>
                  </View>
                )}
                <Text style={styles.dgBody}>
                  Your upgrade starts right away. You&apos;re charged the prorated difference
                  for the rest of this billing period
                  {pendingChange?.setupSessionId
                    ? ' on the card you just added'
                    : pmOnFile ? ` on your ${String(pmOnFile.brand || 'card').toUpperCase()} •••• ${pmOnFile.last4}` : ' on your card on file'}
                  {critsEnabled && upgradeCritPreview(pendingChange?.t) > 0
                    ? `, and ${upgradeCritPreview(pendingChange?.t)} Crits are added to your balance instantly.`
                    : '.'}
                </Text>
              </>
            ) : (
              <>
                <Text style={styles.dgTitle}>
                  {pendingChange?.isIntervalSwitch
                    ? `Switch to ${pendingChangeIv === 'year' ? 'yearly' : 'monthly'} billing?`
                    : 'Reduce your plan?'}
                </Text>
                <Text style={styles.dgBody}>
                  {pendingChange?.isIntervalSwitch
                    ? `Your ${sub?.tierName || currentTier} plan switches to ${pendingChangeIv === 'year' ? 'yearly' : 'monthly'} billing at your next billing date. Your current benefits stay until then${critsEnabled ? ', and switching does not add Crits' : ''}.`
                    : `You're switching to ${pendingChange?.t?.name}. Your current ${sub?.tierName || currentTier} benefits stay until your billing period ends. The change takes effect at your next billing date${critsEnabled ? ', and switching does not add Crits' : ''}.`}
                </Text>
              </>
            )}
            {pendingTerms && (
              <>
                <Text style={styles.dgDisclosure}>{pendingTerms.disclosure}</Text>
                <ConsentCheckbox
                  checked={changeConsent}
                  onToggle={setChangeConsent}
                  label={pendingTerms.consentText}
                  disabled={!!busy || termsBehind}
                />
              </>
            )}
            {pendingChange?.kind === 'upgrade' && (
              <TouchableOpacity
                onPress={() => {
                  // Capture the consent BEFORE startCardUpdate closes the modal.
                  const pc = pendingChange;
                  const renewalConsent = changeRenewalConsent(pc);
                  if (renewalConsent) startCardUpdate(pc, renewalConsent);
                }}
                disabled={!changeConsent || !!busy || termsBehind}
                accessibilityRole="button"
                accessibilityState={{ disabled: !changeConsent }}
                style={[styles.dgCardLinkBtn, !changeConsent && styles.btnDisabled]}
              >
                <Text style={styles.dgCardLink}>Use a different card →</Text>
              </TouchableOpacity>
            )}
            <View style={styles.dgBtnRow}>
              <TouchableOpacity style={styles.dgCancel} onPress={() => setPendingChange(null)}>
                <Text style={styles.dgCancelTxt}>Keep current plan</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.dgConfirm, !changeConsent && styles.btnDisabled]}
                disabled={!changeConsent || !!busy || termsBehind}
                accessibilityRole="button"
                accessibilityState={{ disabled: !changeConsent }}
                onPress={() => {
                  const pc = pendingChange;
                  const renewalConsent = changeRenewalConsent(pc);
                  if (!pc?.t || !renewalConsent) return;
                  setPendingChange(null);
                  applyPlanChange(pc.t, {
                    setupSessionId: pc.setupSessionId || null,
                    renewalConsent,
                    change: { kind: pc.kind, isIntervalSwitch: !!pc.isIntervalSwitch, billing: pc.billing || billing },
                  });
                }}
              >
                <Text style={styles.dgConfirmTxt}>
                  {pendingChange?.isIntervalSwitch ? 'Confirm switch'
                    : pendingChange?.kind === 'upgrade' ? 'Confirm upgrade' : 'Confirm downgrade'}
                </Text>
              </TouchableOpacity>
            </View>
            <ConsentHint visible={!changeConsent} />
          </View>
        </View>
      </Modal>

      {/* Subscribe confirm sheet: the renewal disclosure from the tier list's
          price, then an UNTICKED consent box; "Continue to payment" is the only
          way to createSubscriptionCheckout (13.7). */}
      <Modal visible={!!planConfirm} transparent animationType="fade" onRequestClose={closePlanConfirm}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={closePlanConfirm} />
          <View style={styles.dgCard}>
            <Text style={styles.dgTitle}>{`Start your ${planConfirm?.t?.name || ''} plan`}</Text>
            <Text style={styles.dgPrice}>
              {`${fmtPrice(planConfirm?.amountCents)} a ${planConfirm?.interval === 'year' ? 'year' : 'month'}`}
            </Text>
            <Text style={styles.dgDisclosure}>
              {planConfirm ? planDisclosure(planConfirm.t?.name, planConfirm.amountCents, planConfirm.interval) : ''}
            </Text>
            <ConsentCheckbox
              checked={planConsent}
              onToggle={setPlanConsent}
              label={planConfirm ? planConsentText(planConfirm.interval, planConfirm.amountCents) : ''}
              disabled={!!busy || termsBehind}
            />
            {!!planConfirm?.note && <Text style={styles.dgNote}>{planConfirm.note}</Text>}
            <View style={[styles.dgBtnRow, { marginTop: 12 }]}>
              <TouchableOpacity style={styles.dgCancel} onPress={closePlanConfirm} disabled={!!busy}>
                <Text style={styles.dgCancelTxt}>Not now</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.dgConfirm, !planConsent && styles.btnDisabled]}
                onPress={subscribe}
                disabled={!planConsent || !!busy || termsBehind}
                accessibilityRole="button"
                accessibilityState={{ disabled: !planConsent }}
              >
                {busy && busy === `sub:${planConfirm?.t?.key}`
                  ? <ActivityIndicator size="small" color="#000" />
                  : <Text style={styles.dgConfirmTxt}>Continue to payment</Text>}
              </TouchableOpacity>
            </View>
            <ConsentHint visible={!planConsent} />
          </View>
        </View>
      </Modal>

      {/* Cancel-membership confirmation — on-platform (no Stripe portal). Cancels
          at period end; benefits stay until then, no charge after. */}
      <Modal visible={pendingCancel} transparent animationType="fade" onRequestClose={() => setPendingCancel(false)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setPendingCancel(false)} />
          <View style={styles.dgCard}>
            <Text style={styles.dgTitle}>Cancel your plan?</Text>
            <Text style={styles.dgBody}>
              Your {sub?.tierName || currentTier} benefits stay until{' '}
              {sub?.currentPeriodEnd
                ? new Date(sub.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'the end of your billing period'}
              . After that you move to Free{critsEnabled ? ' (10 Crits/month)' : ''} and won&apos;t be charged again. You can pick a paid plan again anytime.
            </Text>
            <View style={styles.dgBtnRow}>
              <TouchableOpacity style={styles.dgCancel} onPress={() => setPendingCancel(false)}>
                <Text style={styles.dgCancelTxt}>Keep plan</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.dgDanger} onPress={cancelSubscription} disabled={busy === 'cancel'}>
                {busy === 'cancel'
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.dgDangerTxt}>Cancel plan</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Success flair — rain the King Crit dice (same burst as Rumble). */}
      <RumbleBurst
        visible={!!burst}
        title={burst?.title}
        subtitle={burst?.subtitle}
        onDone={() => setBurst(null)}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: C.border,
  },
  backBtn: { flexDirection: 'row', alignItems: 'center' },
  backLabel: { color: C.text, fontSize: 15, fontWeight: '700', marginLeft: 2 },
  headerTitle: { color: C.text, fontSize: 18, fontWeight: '800' },
  pageTitle: { color: C.text, fontSize: 22, fontWeight: '900', marginBottom: 16 },

  // Embedded checkout modal (on-screen, no redirect)
  checkoutOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  checkoutScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  checkoutCard: { width: '100%', maxWidth: 480, maxHeight: '88%', backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden' },
  checkoutHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#0c0c0c' },
  checkoutTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  checkoutClose: { color: '#fff', fontSize: 18, fontWeight: '700' },
  checkoutScroll: { backgroundColor: '#fff' },
  checkoutDisclosure: { color: '#333', fontSize: 12.5, lineHeight: 18, paddingHorizontal: 16, paddingTop: 12 },
  scroll: { padding: 16, paddingBottom: 48 },

  statusCard: {
    flexDirection: 'row', alignItems: 'center', position: 'relative', overflow: 'hidden', minHeight: 116,
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 18, marginBottom: 24,
  },
  statusLabel: { color: C.textMuted, fontSize: 13 },
  critBalance: { color: C.brand, fontSize: 36, fontWeight: '900', marginVertical: 2 },
  statusSub: { color: C.textMuted, fontSize: 13 },
  manageBtn: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingHorizontal: 16, paddingVertical: 10,
  },
  manageBtnText: { color: C.text, fontWeight: '700' },

  sectionTitle: { color: C.text, fontSize: 18, fontWeight: '800', marginBottom: 4 },
  sectionHint: { color: C.textMuted, fontSize: 13, marginBottom: 14 },

  billingToggle: {
    flexDirection: 'row', alignSelf: 'flex-start', borderWidth: 1, borderColor: C.borderStrong || '#333',
    borderRadius: 999, overflow: 'hidden', marginBottom: 14,
  },
  billingOpt: { paddingHorizontal: 16, paddingVertical: 8 },
  billingOptOn: { backgroundColor: C.brand },
  billingOptTxt: { color: C.textMuted, fontSize: 12.5, fontWeight: '800' },
  billingOptTxtOn: { color: '#000' },
  yearlyNote: { color: C.brand, fontSize: 12, fontWeight: '700', marginTop: 4 },

  founderScarcity: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    backgroundColor: 'rgba(0,207,255,0.07)', borderWidth: 1, borderColor: 'rgba(0,207,255,0.45)',
    borderRadius: THEME.radius.md, padding: 12, marginBottom: 14,
  },
  founderScarcityTxt: { flex: 1, color: '#cfeffb', fontSize: 12.5, lineHeight: 18 },
  founderScarcityStrong: { color: '#aff0ff', fontWeight: '800' },

  tierCard: {
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 16, marginBottom: 12,
  },
  tierCardCurrent: { borderColor: C.brand },
  tierTop: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  tierName: { color: C.text, fontSize: 18, fontWeight: '800' },
  tierPrice: { color: C.brand, fontSize: 16, fontWeight: '800' },
  tierPerks: { marginTop: 10, marginBottom: 14 },
  perk: { color: C.textMuted, fontSize: 14, lineHeight: 22 },
  subscribeBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md,
    paddingVertical: 12, alignItems: 'center',
  },
  subscribeBtnText: { color: '#000', fontWeight: '800', fontSize: 15 },
  btnDisabled: { opacity: 0.45 },

  // Stored Terms behind: purchases wait for the review (13.7)
  termsBanner: {
    flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10,
    backgroundColor: 'rgba(252,208,0,0.07)', borderWidth: 1, borderColor: 'rgba(252,208,0,0.45)',
    borderRadius: THEME.radius.md, padding: 12, marginBottom: 16,
  },
  termsBannerTxt: { flex: 1, minWidth: 180, color: C.text, fontSize: 13, lineHeight: 19 },
  termsBannerBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingHorizontal: 14, paddingVertical: 8,
  },
  termsBannerBtnTxt: { color: '#000', fontWeight: '800', fontSize: 13 },
  currentRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  currentBadge: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  currentBadgeText: { color: C.brand, fontWeight: '700', marginLeft: 6 },
  cancelLink: { color: '#ff6b6b', fontSize: 13, fontWeight: '700' },
  cancelScheduled: { color: C.textMuted, fontSize: 12.5, fontWeight: '600' },

  packCard: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 16, marginBottom: 12,
  },
  packName: { color: C.text, fontSize: 15, fontWeight: '800' },
  packDesc: { color: C.textMuted, fontSize: 13, marginTop: 4, lineHeight: 18 },
  buyBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md,
    paddingHorizontal: 16, paddingVertical: 12, minWidth: 76, alignItems: 'center',
  },
  buyBtnText: { color: '#000', fontWeight: '800', fontSize: 15 },

  footnote: { color: C.textMuted, fontSize: 12, marginTop: 20, lineHeight: 18 },

  // Downgrade confirm modal
  dgOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  dgScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  dgCard: {
    width: '100%', maxWidth: 400, backgroundColor: C.surface, borderWidth: 1, borderColor: C.borderStrong || '#333',
    borderRadius: THEME.radius.lg, padding: 20,
  },
  dgTitle: { color: C.text, fontSize: 17, fontWeight: '800', marginBottom: 10 },
  dgBody: { color: C.textMuted, fontSize: 13.5, lineHeight: 20, marginBottom: 10 },
  dgCardLinkBtn: { alignSelf: 'flex-start', marginTop: 4, marginBottom: 16 },
  dgCardLink: { color: C.brand, fontSize: 13, fontWeight: '700' },
  dgPrice: { color: C.text, fontSize: 15, fontWeight: '800', marginBottom: 8 },
  dgDisclosure: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 2 },
  dgNote: { color: '#ff9c9c', fontSize: 12.5, lineHeight: 18, marginTop: 8 },
  dgDeclined: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: 'rgba(255,107,107,0.08)', borderWidth: 1, borderColor: 'rgba(255,107,107,0.45)',
    borderRadius: THEME.radius.md, padding: 10, marginBottom: 12,
  },
  dgDeclinedTxt: { flex: 1, color: '#ff9c9c', fontSize: 12.5, lineHeight: 18 },
  dgBtnRow: { flexDirection: 'row', gap: 10 },
  dgCancel: {
    flex: 1, backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong || '#333',
    borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dgCancelTxt: { color: C.text, fontWeight: '700', fontSize: 13.5 },
  dgConfirm: { flex: 1, backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgConfirmTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  dgDanger: { flex: 1, backgroundColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgDangerTxt: { color: '#fff', fontWeight: '800', fontSize: 13.5 },
});
